import { useState } from 'react';
import { Button, ComboboxExternal, ComboboxItem, lucideIcons, Popover, toast } from '@round/creatorbase-ui';
import { useBulkUpdateOutreachResultsMutation, useClientContentTags } from '@round/creatorbase-helpers/api-hooks';
import useClientSelect from 'Modules/Plans/Clients/hooks/useClientSelect';
import { mapContentTagsToOptions } from 'helpers';
import { usePatchOutreachResultsCache } from '../../../../hooks/usePatchOutreachResultsCache';
import styles from './BulkTagsPopover.module.css';

const { Tags } = lucideIcons;

const SEARCH_PLACEHOLDER = 'Search tags…';
const NO_TAGS_FOUND = 'No tags found';
const LOADING = 'Loading...';
const APPLY_LABEL = 'Apply';

const PICKER_LIST_SELECTOR = '[data-slot="combobox-content"]';

const pluralizeCreators = (count: number) => `${count} ${count === 1 ? 'creator' : 'creators'}`;

type TagItem = ComboboxItem<number>;

type Props = {
    outreachRequestId: number;
    /** The selected results still in review, which are the only ones the endpoint tags. */
    resultIds: number[];
    label: string;
    disabled: boolean;
    onApplied: () => void;
};

/** Adds the picked tags to every selected result in review, on top of the tags each already has. */
export const BulkTagsPopover = ({ outreachRequestId, resultIds, label, disabled, onApplied }: Props) => {
    const { selectedClientId } = useClientSelect();
    const { tags, isLoading: areTagsLoading } = useClientContentTags(selectedClientId);
    const { bulkUpdateAsync, isLoading: isSaving, helpers } = useBulkUpdateOutreachResultsMutation();
    const patchResultsCache = usePatchOutreachResultsCache();

    const [isOpen, setIsOpen] = useState(false);
    const [draft, setDraft] = useState<TagItem[]>([]);

    const handleOpenChange = (open: boolean) => {
        setIsOpen(open);
        if (open) {
            setDraft([]);
        }
    };

    const handleApply = async () => {
        try {
            const { results, skipped } = await bulkUpdateAsync({
                outreach_request_id: outreachRequestId,
                result_ids: resultIds,
                content_tags: draft.map((item) => item.value),
            });

            patchResultsCache(results);

            if (results.length) {
                toast.success(`Tagged ${pluralizeCreators(results.length)}`);
            }

            if (skipped.length) {
                toast.error(
                    skipped.length === 1 ? skipped[0].reason : `Could not tag ${pluralizeCreators(skipped.length)}`
                );
            }

            setIsOpen(false);
            onApplied();
        } catch (error) {
            toast.error(helpers.getErrorMessage(error as Error));
        }
    };

    return (
        <Popover open={isOpen} onOpenChange={handleOpenChange} modal={false}>
            <Popover.Trigger asChild>
                <Button variant="outline" size="xs" disabled={disabled || isSaving}>
                    <Tags className={styles.icon} aria-hidden="true" />
                    {label}
                </Button>
            </Popover.Trigger>
            <Popover.Content
                align="end"
                className={styles.content}
                // The picker's list is portaled outside the popover's own content, so a pick in it
                // would otherwise read as a click outside and close the popover under it.
                onInteractOutside={(event) => {
                    if ((event.target as Element | null)?.closest(PICKER_LIST_SELECTOR)) {
                        event.preventDefault();
                    }
                }}
            >
                {/* Apply heads the popover, since the tag list opens downward over whatever is below it. */}
                <div className={styles.header}>
                    <span className={styles.title}>{`Add tags to ${pluralizeCreators(resultIds.length)}`}</span>
                    <Button size="xs" onClick={handleApply} disabled={!draft.length || isSaving} isLoading={isSaving}>
                        {APPLY_LABEL}
                    </Button>
                </div>
                <ComboboxExternal
                    multiple
                    autoHighlight
                    items={mapContentTagsToOptions(tags)}
                    isItemEqualToValue={(a, b) => a.value === b.value}
                    value={draft}
                    onValueChange={setDraft}
                    placeholder={SEARCH_PLACEHOLDER}
                    noItemsFound={areTagsLoading ? LOADING : NO_TAGS_FOUND}
                    // Flipping up would put the list over Apply, so it stays below and scrolls instead.
                    collisionAvoidance={{ side: 'none', fallbackAxisSide: 'none' }}
                />
            </Popover.Content>
        </Popover>
    );
};
